import React, { MouseEvent, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import { allCountries } from 'country-telephone-data';
import { useSnackbar } from 'notistack';
import { Controller, SubmitErrorHandler, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuid_v4 } from 'uuid';
import { countries } from '~/globals';
import {
  isCountryRequiredItemType,
  isCruiseItemType,
  isReasonOtherTextRequired,
  isTourOrCruiseItemType,
} from '~/validation/schemas/customOfferValidationPredicates';
import { customOfferSchema, editableCustomOfferSchema } from '~/validation/schemas/customOffers';

import CheckIcon from '@mui/icons-material/Check';
import HelpIcon from '@mui/icons-material/Help';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { getCurrencyCodes, getRegionByCurrency } from '@luxuryescapes/lib-regions';

import DateWidget from '~/components/Common/Elements/DateWidget';
import OrderDetailNotes from '~/components/Purchases/OrderDetail/OrderDetailNotes';

import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';
import { OPERATIONS_SUPPORT } from '~/consts/notes';
import { OPERATION_REBOOK_AS_CUSTOM_OFFER } from '~/consts/order';
import stores from '~/consts/stores';
import { UTM_MEDIUM } from '~/consts/utm';

import { useGetEmployee } from '~/hooks/apis/business-traveller/useGetEmployee';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useToggleState from '~/hooks/useToggleState';

import { getEmployee, getEmployeeList } from '~/services/BusinessTraveller/BusinessTravellerService';
import { sendBookingPaymentLink, sendBookingPaymentLinkSms } from '~/services/NotificationService';
import OffersService from '~/services/OffersService';
import * as OrdersService from '~/services/OrdersService';
import { addDays, formatDateISO, isBefore, newDate } from '~/services/TimeService';

import { buildServiceFeeOrderItem } from '~/utils/businessTraveller/serviceFee';
import {
  CurrencyDict,
  calculateCustomerSellPriceWithFx,
  deriveCustomerCurrency,
  deriveLeEntity,
  fetchSpotAndContractedRates,
} from '~/utils/customOfferFormUtils';

import ImagesForm from '../Forms/ImagesForm';
import { RichTextEditor } from '../Forms/fields';

import { VccCreatedDisableFieldHelperText } from './Components/VccCreatedDisableFieldHelperText';
import CustomOfferBusinessTravellerServiceFeeSection from './CustomOfferBusinessTravellerServiceFeeSection';
import AccountsPayable from './CustomOfferForm/AccountsPayable';
import { CancellationPolicies } from './CustomOfferForm/CancellationPolicies';
import CruisePostPurchaseCoreDetails from './CustomOfferForm/CruisePostPurchase/CruisePostPurchaseCoreDetails';
import CruisePostPurchaseSummary from './CustomOfferForm/CruisePostPurchase/CruisePostPurchaseSummary';
import CruisePostPurchaseTypeFields from './CustomOfferForm/CruisePostPurchase/CruisePostPurchaseTypeFields';
import { getCustomOfferFieldLabels } from './CustomOfferForm/CruisePostPurchase/config';
import { buildCruisePayload } from './CustomOfferForm/CruisePostPurchase/payload';
import { useCruisePostPurchase } from './CustomOfferForm/CruisePostPurchase/useCruisePostPurchase';
import CustomerPaymentsSection from './CustomOfferForm/CustomerPaymentsSection';
import { PrePopulateCustomOfferForm } from './CustomOfferForm/PrePopulateCustomOfferForm';
import RatesTable from './CustomOfferForm/RatesTable';
import CustomOfferGstWidget from './CustomOfferGstWidget';
import CustomOfferInternalDiscountField from './CustomOfferInternalDiscountField';
import CustomOfferLoyaltyCalculator from './CustomOfferLoyaltyCalculator';
import {
  CustomOfferLinkedPackageOfferField,
  CustomOfferPackageComponentsSection,
} from './CustomOfferPackageAssociations';
import { CustomOfferRequiredFieldsLegend } from './CustomOfferRequiredFieldsLegend';
import { TravellersSection, TravellersSectionRef } from './CustomOfferTravellersSection';
import { HtmlTooltip } from './HTMLTooltip';
import { Property, PropertyAutocomplete, PropertyImage } from './PropertyAutocomplete';
import CustomOfferVendorVirtualCreditCardSection from './VirtualCreditCards/CustomOfferVendorVirtualCreditCardSection';
import { CustomOfferVendorVirtualCreditCardSectionHeading } from './VirtualCreditCards/CustomOfferVendorVirtualCreditCardSectionHeading';
import { WholesaleAgentAutocomplete } from './WholesaleAgentAutocomplete';
import {
  CONCIERGE_AGENT_LIST,
  CRUISE_ITEM_TYPE,
  DEFAULT_CRUISE_POST_PURCHASE_LE_FEE,
  DEFAULT_ITEM_TYPE,
  FLEXIBLE_CANCELLATION_POLICY,
  LEBT_DEFAULT_ITEM_TYPE,
  LE_ENTITY_AUS,
  LE_ENTITY_INTL,
  NONREFUNDABLE_CANCELLATION_POLICY,
  OFFER_TYPES,
  PURCHASE_TYPE_POST_PURCHASE,
  PURCHASE_TYPE_SALES,
  REASONS,
  SALES_ATTRIBUTION_MARGIN_MAX_VALUE,
  WHOLESALE_AGENT_LIST,
  getSalesAttributionList,
} from './constants';
import { buildCustomOfferPaymentPayloadV2 } from './customOfferOrderPayload';
import { getOfferTypeSelectOptions, isOfferTypeSelectLocked } from './customOfferPackagePermissions';
import { ConfirmDeleteDialog } from './dialogs/ConfirmDeleteDialog';
import { PropertyImagePrefillDialog } from './dialogs/PropertyImagePrefillDialog';
import { buildPendingPointsPayload, fetchLoyaltyTier } from './loyaltyUtils';
import { sendManualV2TrackingAlert } from './manualV2TrackingAlert';
import {
  PaymentFlowType,
  PaymentFlowV2FieldErrorMap,
  buildCustomerPaymentMetadata,
  calculateLegacyCustomerFinalPaymentAmount,
  calculatePaymentFlowV2DerivedAmounts,
  deriveDueDateFromPaymentFlowV2,
  getCustomerPaymentSectionState,
  hydrateLegacyCustomerPaymentFormValues,
  hydratePaymentFlowV2FormValues,
} from './paymentFlowUtils';
import { getIsChangedFromLeLiveOffer, isFreeTextReason, placeCustomOfferOrder } from './utils';

const getFirstValidationErrorMessage = (value: unknown): string | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  if ('message' in value && typeof value.message === 'string') {
    return value.message;
  }

  for (const nestedValue of Object.values(value)) {
    const message = getFirstValidationErrorMessage(nestedValue);
    if (message) {
      return message;
    }
  }

  return undefined;
};

import CustomOfferType = App.CustomOfferType;

const phonePrefixOptions: Array<{ value: string; label: string }> = allCountries.map(({ dialCode, name }) => ({
  value: dialCode,
  label: `+${dialCode} ${name}`,
}));

type Props = {
  user: App.User;
  currentOffer?: App.CustomOffer;
  hasOrders?: boolean;
  order?: App.Order;
  onUpdate?: (offer: App.CustomOffer) => Promise<void>;
  paymentDate?: string;
  changeFormType: (
    type: App.CustomOfferType,
    internalDiscountTracking?: string | null,
    nextVendorName?: string,
  ) => void;
  offerTypes: typeof OFFER_TYPES;
  selectedItemType: App.CustomOfferType;
  brand: App.Brands;
  travellersSectionRef: RefObject<TravellersSectionRef>;
  isBusinessTraveller: boolean;
  customerHasPaid: boolean;
};

const buildDefaultValues = (
  brand: string,
  offer?: App.CustomOffer,
  userCountryCode?: string,
): CustomOfferFormValues => {
  const today = new Date().toISOString().split('T')[0];
  // currently we support only one item per offer
  const item = offer?.items[0];
  const travellers = offer?.items[0]?.travellers;
  const isBusinessTraveller = brand === LE_BUSINESS_TRAVELLER;
  const isCruisePostPurchaseOffer =
    item?.type === CRUISE_ITEM_TYPE && offer?.purchase_type === PURCHASE_TYPE_POST_PURCHASE;
  const defaultCruiseLeFee = isCruisePostPurchaseOffer ? DEFAULT_CRUISE_POST_PURCHASE_LE_FEE : 0;
  const defaultPaymentType = isBusinessTraveller ? 'full_payment' : 'deposit';
  const defaultItemType = isBusinessTraveller ? LEBT_DEFAULT_ITEM_TYPE : DEFAULT_ITEM_TYPE;
  const paymentFlowV2 = offer?.payment_metadata?.payment_flow_v2;
  const hydratedPaymentFlowV2 = hydratePaymentFlowV2FormValues(paymentFlowV2, !offer);
  const isLegacyCustomerPaymentFlow = !!offer && !hydratedPaymentFlowV2.type;
  const hydratedLegacyCustomerPayment = hydrateLegacyCustomerPaymentFormValues({
    paymentMetadata: offer?.payment_metadata,
    isLegacyFlow: isLegacyCustomerPaymentFlow,
    defaultPaymentType,
    today,
  });

  let reasonDefaultValue, reasonOtherDefaultValue;
  if (offer?.reason && isFreeTextReason(offer.reason)) {
    reasonDefaultValue = 'other';
    reasonOtherDefaultValue = offer.reason;
  } else {
    reasonDefaultValue = offer?.reason;
  }

  const defaultPriceCurrency =
    !offer && userCountryCode ? (deriveCustomerCurrency(brand, userCountryCode) ?? 'AUD') : 'AUD';

  // Non-LEBT brands
  const leServiceFee = isBusinessTraveller ? 0 : (offer?.payment_metadata?.le_service_fee ?? 0) / 100;

  // LEBT service fee, amount is in dollars.
  const lebtServiceFee = offer?.payment_metadata?.lebt_service_fee;

  const defaultValues: CustomOfferFormValues = {
    item: {
      type: item?.type ?? defaultItemType,
      name: item?.name ?? '',
      city: item?.city ?? '',
      country: item?.country ?? '',
      description: item?.description ?? '',
      vendor: item?.vendor ?? '',
      vendor_reference_number: item?.vendor_reference_number || '',
      salesforce_case_number: item?.salesforce_case_number || '',
      service_information: item?.service_information ?? '',
      start_date: item?.start_date ?? today,
      end_date: item?.end_date ?? formatDateISO(addDays(1)),
    },
    travellers:
      travellers?.length > 0
        ? travellers
        : brand === LE_BUSINESS_TRAVELLER
          ? [
              {
                first_name: '',
                last_name: '',
                email: '',
                date_of_birth: today,
                type: 'adult',
                country: '',
                employee: null,
              },
            ]
          : [{ name: '', date_of_birth: today, type: 'adult', country: '' }],
    price: offer?.price ? offer.price / 100 : 0,
    price_currency: offer?.price_currency ?? defaultPriceCurrency,
    cost_price: offer?.cost_price ? offer.cost_price / 100 : 0,
    cost_price_currency: offer?.cost_price_currency ?? 'AUD',
    due_date: offer?.due_date ?? formatDateISO(addDays(1)),
    sold_at: offer?.sold_at ?? today,
    payment_terms: offer?.payment_terms ?? 'vcc',
    images: offer?.images ?? [],
    payment_metadata: {
      ...hydratedLegacyCustomerPayment,
      margin: offer?.payment_metadata?.margin / 100 ? offer.payment_metadata?.margin / 100 : 0,
      le_service_fee: leServiceFee,
      cruiseline_fee: (offer?.payment_metadata?.cruiseline_fee ?? 0) / 100,
      cruiseline_additional_charges: (offer?.payment_metadata?.cruiseline_additional_charges ?? 0) / 100,
      le_fee: offer?.payment_metadata?.le_fee != null ? offer.payment_metadata.le_fee / 100 : defaultCruiseLeFee,
      refare_new_fare: (offer?.payment_metadata?.refare_new_fare ?? 0) / 100,
      refare_new_tax_and_surcharges: (offer?.payment_metadata?.refare_new_tax_and_surcharges ?? 0) / 100,
      refare_deposit_fee_already_paid: (offer?.payment_metadata?.refare_deposit_fee_already_paid ?? 0) / 100,
      lebt_service_fee: lebtServiceFee,

      // customer Fields
      customer_exchange_rate: offer?.payment_metadata?.customer_exchange_rate ?? 1,
      customer_paid: offer?.payment_metadata?.customer_paid ?? false,
      payment_flow_v2: hydratedPaymentFlowV2,
      block_customer_payment_notifications: offer?.payment_metadata?.block_customer_payment_notifications ?? false,
      merchant_fee_enabled: offer?.payment_metadata?.merchant_fee_enabled ?? true,

      // vendor fields
      vendor_payment_type: offer?.payment_metadata?.vendor_payment_type ?? 'deposit',
      vendor_exchange_rate: offer?.payment_metadata?.vendor_exchange_rate ?? 1,
      vendor_due_date: offer?.payment_metadata?.vendor_due_date ?? formatDateISO(addDays(1)),
      vendor_was_paid: offer?.payment_metadata?.vendor_was_paid ?? false,
      vendor_deposit_1_amount:
        offer?.payment_metadata?.vendor_payment_type === 'deposit'
          ? (offer?.payment_metadata?.vendor_deposit_1_amount ?? 0) / 100
          : 0,
      vendor_deposit_1_due_date:
        offer?.payment_metadata?.vendor_payment_type === 'deposit'
          ? (offer?.payment_metadata?.vendor_deposit_1_due_date ?? today)
          : undefined,
      vendor_deposit_payment_made:
        offer?.payment_metadata?.vendor_payment_type === 'deposit'
          ? (offer?.payment_metadata?.vendor_deposit_payment_made ?? false)
          : undefined,
      final_payment_vcc_digits:
        offer?.payment_terms === 'vcc' ? offer?.payment_metadata?.final_payment_vcc_digits : undefined,
      deposit_vcc_digits:
        offer?.payment_terms === 'vcc' || offer?.payment_metadata?.payment_type === 'deposit'
          ? offer?.payment_metadata?.deposit_vcc_digits
          : undefined,
      final_amount_vendor: offer?.payment_metadata?.final_amount_vendor
        ? offer.payment_metadata?.final_amount_vendor / 100
        : 0,
      // set it to today to allow editing of pre-field offers
      vendor_payment_date:
        offer?.payment_metadata?.vendor_was_paid && offer?.payment_metadata?.vendor_payment_date
          ? offer?.payment_metadata.vendor_payment_date
          : today,
      vendor_deposit_payment_date:
        offer?.payment_metadata?.vendor_deposit_payment_made && offer?.payment_metadata?.vendor_deposit_payment_date
          ? offer?.payment_metadata.vendor_deposit_payment_date
          : today,
      refund_received: offer?.payment_metadata?.refund_received ?? null,
      le_entity: offer?.payment_metadata?.le_entity ?? deriveLeEntity(defaultPriceCurrency),
      commission_received: offer?.payment_metadata?.commission_received ?? false,
      commission_amount: offer?.payment_metadata?.commission_amount
        ? offer?.payment_metadata?.commission_amount / 100
        : 0,
      additional_vendor_payment: offer?.payment_metadata?.additional_vendor_payment
        ? offer.payment_metadata.additional_vendor_payment / 100
        : 0,
      vendor_is_non_refundable: offer?.payment_metadata?.vendor_is_non_refundable ?? false,
      created_by: offer?.payment_metadata?.created_by ?? '',
      sales_agent_name: offer?.payment_metadata?.sales_agent_name ?? '',
      is_rebooking: offer?.payment_metadata?.is_rebooking ?? false,
      rebooking_from_order_id: offer?.payment_metadata?.rebooking_from_order_id ?? '',
      cancellation_end_date: offer?.payment_metadata?.cancellation_end_date ?? null,
      cancellation_type: offer?.payment_metadata?.cancellation_type ?? NONREFUNDABLE_CANCELLATION_POLICY,
      sales_attribution: offer?.payment_metadata?.sales_attribution ?? UTM_MEDIUM,
      additional_margin:
        offer?.payment_metadata?.additional_margin != null ? offer.payment_metadata.additional_margin / 100 : null,
      reason_additional_info: offer?.payment_metadata?.reason_additional_info,
      reason_issue_raised: offer?.payment_metadata?.reason_issue_raised,
      internal_discount_tracking: offer?.payment_metadata?.internal_discount_tracking ?? '',
      price_includes_gst: offer?.payment_metadata?.price_includes_gst ?? false,
      gst_amount: (offer?.payment_metadata?.gst_amount ?? 0) / 100,
      final_amount_customer_display:
        offer?.payment_metadata?.final_amount_customer_display != null
          ? offer.payment_metadata.final_amount_customer_display / 100
          : 0,
    },
    notes: offer?.notes ?? '',
    created_from_booking_number: offer?.created_from_booking_number ?? null,
    created_from_item_id: offer?.created_from_item_id ?? null,
    customer_phone_prefix: offer?.customer_phone_prefix ?? '',
    customer_phone_number: offer?.customer_phone_number ?? '',
    fk_package_custom_offer_id: offer?.fk_package_custom_offer_id ?? null,
    reason: reasonDefaultValue || '',
    purchase_type: offer?.purchase_type ?? PURCHASE_TYPE_SALES,
    post_purchase_type: offer?.post_purchase_type ?? '',
    reasonOther: reasonOtherDefaultValue || '',
    supplier: 'Other',
    hiddenFromCustomer: offer?.hidden_from_customer ?? false,
    sentToAtiTicketing: offer?.sent_to_ati_ticketing ?? false,
  };

  if (defaultValues.item.type === 'cruise') {
    defaultValues.item.cruise_line = item?.cruise_line ?? '';
    defaultValues.item.ship_name = item?.ship_name ?? '';
    defaultValues.item.ship_cabin = item?.ship_cabin ?? '';
    defaultValues.item.ship_rate = item?.ship_rate ?? '';
  }

  return defaultValues;
};

type Payload = Omit<App.CustomOffer, 'id' | 'status' | 'has_vcc' | 'created_at' | 'updated_at'>;

const buildPayload = (user: App.User, formData: CustomOfferFormValues, images): Payload => {
  const {
    item,
    travellers,
    payment_metadata,
    purchase_type,
    post_purchase_type,
    reason,
    reasonOther,
    hiddenFromCustomer,
    sentToAtiTicketing,
    ...rest
  } = formData;
  const customerPaymentMetadata = buildCustomerPaymentMetadata(payment_metadata);
  const cruisePayload = buildCruisePayload(
    item,
    payment_metadata,
    purchase_type ?? PURCHASE_TYPE_SALES,
    sentToAtiTicketing ?? false,
    post_purchase_type,
  );
  const gstAmountInCents = Math.round((payment_metadata?.gst_amount ?? 0) * 100);
  const payload: Payload = {
    ...rest,
    hidden_from_customer: hiddenFromCustomer,
    ...(cruisePayload?.offerFields ?? {}),
    price: Math.round(rest.price * 100),
    cost_price: Math.round(rest.cost_price * 100),
    // due_date will the instance of JS Date due to Yup validation and schema conversion
    due_date:
      cruisePayload?.offerFields.purchase_type === PURCHASE_TYPE_POST_PURCHASE
        ? deriveDueDateFromPaymentFlowV2(
            payment_metadata.payment_flow_v2,
            rest.due_date != null && rest.due_date !== '' ? formatDateISO(rest.due_date) : undefined,
          )
        : formatDateISO(rest.due_date),
    sold_at: formatDateISO(rest.sold_at),
    payment_metadata: {
      customer_exchange_rate: payment_metadata.customer_exchange_rate,
      customer_paid: payment_metadata.customer_paid ?? false,
      block_customer_payment_notifications: payment_metadata.block_customer_payment_notifications ?? false,
      merchant_fee_enabled: payment_metadata.merchant_fee_enabled ?? true,
      vendor_payment_type: payment_metadata.vendor_payment_type,
      vendor_exchange_rate: payment_metadata.vendor_exchange_rate,
      vendor_due_date: formatDateISO(payment_metadata.vendor_due_date),
      vendor_was_paid: payment_metadata.vendor_was_paid ?? false,
      vendor_deposit_1_amount: Math.round(payment_metadata.vendor_deposit_1_amount * 100),
      vendor_deposit_1_due_date:
        payment_metadata.vendor_payment_type === 'deposit'
          ? formatDateISO(payment_metadata.vendor_deposit_1_due_date)
          : null,
      vendor_deposit_payment_made: payment_metadata.vendor_deposit_payment_made ?? false,
      deposit_vcc_digits: payment_metadata.deposit_vcc_digits,
      final_payment_vcc_digits: payment_metadata.final_payment_vcc_digits,
      ...customerPaymentMetadata,
      final_amount_vendor: Math.round(payment_metadata.final_amount_vendor * 100),
      le_service_fee: Math.round(payment_metadata.le_service_fee * 100),
      margin: Math.round(payment_metadata.margin * 100),
      // LEBT service fee, amount is in dollars.
      ...(payment_metadata.lebt_service_fee && { lebt_service_fee: payment_metadata.lebt_service_fee }),
      // ensure payments have been made before sending payment dates
      vendor_payment_date:
        payment_metadata?.vendor_payment_date && payment_metadata?.vendor_was_paid
          ? formatDateISO(payment_metadata.vendor_payment_date)
          : null,
      vendor_deposit_payment_date:
        payment_metadata?.vendor_deposit_payment_date && payment_metadata?.vendor_deposit_payment_made
          ? formatDateISO(payment_metadata.vendor_deposit_payment_date)
          : null,
      refund_received: payment_metadata?.refund_received ? formatDateISO(payment_metadata?.refund_received) : undefined,
      le_entity: payment_metadata?.le_entity,
      commission_received: payment_metadata?.commission_received,
      commission_amount: payment_metadata.commission_amount ? Math.round(payment_metadata.commission_amount * 100) : 0,
      additional_vendor_payment: payment_metadata.additional_vendor_payment
        ? Math.round(payment_metadata.additional_vendor_payment * 100)
        : 0,
      created_by: payment_metadata.created_by,
      sales_agent_name: payment_metadata.sales_agent_name,
      sales_attribution: payment_metadata.sales_attribution,
      is_rebooking: payment_metadata.is_rebooking ?? false,
      rebooking_from_order_id: payment_metadata.rebooking_from_order_id,
      cancellation_type: payment_metadata?.cancellation_type,
      cancellation_end_date:
        payment_metadata?.cancellation_type === FLEXIBLE_CANCELLATION_POLICY
          ? formatDateISO(payment_metadata?.cancellation_end_date)
          : undefined,
      vendor_is_non_refundable: payment_metadata?.vendor_is_non_refundable ?? false,
      additional_margin:
        payment_metadata.additional_margin != null ? Math.round(payment_metadata.additional_margin * 100) : null,
      internal_discount_tracking: payment_metadata.internal_discount_tracking?.trim()
        ? payment_metadata.internal_discount_tracking.trim()
        : null,
      reason_additional_info: payment_metadata?.reason_additional_info,
      reason_issue_raised: payment_metadata?.reason_issue_raised,
      service_fee_type: payment_metadata.service_fee_type,
      price_includes_gst: payment_metadata?.price_includes_gst ?? false,
      gst_amount: gstAmountInCents,
      final_amount_customer_display:
        payment_metadata?.final_amount_customer_display != null
          ? Math.round(payment_metadata.final_amount_customer_display * 100)
          : null,
      ...(cruisePayload?.paymentMetadata ?? {}),
    },
    name: item.name,
    fk_customer_id: user.id_member,
    customer_full_name: user.fullName,
    images: images,
    items: [
      {
        ...item,
        // start_date and end_date will the instance of JS Date due to Yup validation and schema conversion
        start_date: formatDateISO(item.start_date),
        end_date: formatDateISO(item.end_date),
        cruise_line: item?.type === CRUISE_ITEM_TYPE ? item?.cruise_line : '',
        ship_name: item?.type === CRUISE_ITEM_TYPE ? item?.ship_name : '',
        ship_cabin: item?.type === CRUISE_ITEM_TYPE ? item?.ship_cabin : '',
        ship_rate: item?.type === CRUISE_ITEM_TYPE ? item?.ship_rate : '',
        gst_amount: gstAmountInCents / 100,
        travellers: travellers.map((traveller) => ({
          ...traveller,
          name: traveller.name?.trim() === '' ? null : traveller.name,
          date_of_birth: traveller.date_of_birth ? formatDateISO(traveller.date_of_birth) : null,
        })),
      },
    ],
  };

  if (cruisePayload?.offerFields.purchase_type === PURCHASE_TYPE_POST_PURCHASE) {
    payload.created_from_booking_number = rest.created_from_booking_number || undefined;
    payload.created_from_item_id = undefined;
  } else if (!(rest.created_from_booking_number && rest.created_from_item_id)) {
    payload.created_from_booking_number = undefined;
    payload.created_from_item_id = undefined;
  }

  // held in reasonOther
  if (reason && reason === 'other' && reasonOther) {
    payload.reason = reasonOther;
  } else {
    payload.reason = reason ?? undefined;
  }

  return payload;
};

const buildPaymentPayload = async (
  user: App.User,
  offer: App.CustomOffer,
  itemType: App.CustomOfferType,
  brand: App.Brands = 'luxuryescapes',
) => {
  const transactionKey = uuid_v4();
  const total = offer.payment_metadata.final_amount_customer / 100;
  const gstAmount = offer.payment_metadata.gst_amount ? offer.payment_metadata.gst_amount / 100 : 0;
  const vendorTotal = offer.payment_metadata.final_amount_vendor / 100;
  const regionCode = getRegionByCurrency(offer.price_currency, brand);

  let employee = undefined;
  if (brand === LE_BUSINESS_TRAVELLER) {
    employee = await getEmployee(user.id_member);
  }

  const isCustomOfferPackage = itemType === 'custom-offer-package';
  const allowsZeroVendorTotal = isCruiseItemType(itemType) && offer.purchase_type === PURCHASE_TYPE_POST_PURCHASE;
  if (!isCustomOfferPackage && (total <= 0 || vendorTotal < 0 || (vendorTotal === 0 && !allowsZeroVendorTotal))) {
    throw new Error('Customer and Vendor total amount must be greater than 0');
  }

  const pendingPoints = await buildPendingPointsPayload(
    user,
    offer,
    transactionKey,
    offer.payment_metadata?.sales_attribution,
    brand,
  );
  const tier = pendingPoints ? await fetchLoyaltyTier(user.id_member) : null;

  // LEBT appends service_fee_item to the order payload
  const serviceFeeItem = brand === LE_BUSINESS_TRAVELLER ? buildServiceFeeOrderItem(offer.payment_metadata) : null;

  return {
    brand,
    business_id: employee?.businessId ?? undefined,
    currency_code: offer.price_currency,
    region_code: regionCode.code,
    customer_id: user.id_member,
    customer_email: user.email,
    customer_phone_prefix: offer.customer_phone_prefix,
    customer_phone: offer.customer_phone_number,
    utm_source: brand,
    utm_medium: offer?.payment_metadata?.sales_attribution ?? UTM_MEDIUM,
    transaction_key: transactionKey,
    type: 'custom_offer',
    ...(brand === LE_BUSINESS_TRAVELLER && {
      concierge_mode: offer.payment_metadata.lebt_service_fee?.concierge_mode ?? false,
    }),
    items: [
      ...offer.items.map((item) => ({
        ...item,
        transaction_key: transactionKey,
        type: 'custom_offer' as unknown as CustomOfferType,
        custom_offer_id: offer.id,
        due_date: offer.due_date,
        total,
        gst_amount: gstAmount,
        custom_item_type: item.type,
      })),
      ...(serviceFeeItem ? [serviceFeeItem] : []),
    ],
    ...(pendingPoints && tier && { lux_loyalty: { pendingPoints, tier } }),
  };
};

export default function CustomOfferForm({
  user,
  hasOrders,
  order,
  onUpdate,
  paymentDate,
  currentOffer,
  changeFormType,
  offerTypes,
  selectedItemType,
  brand,
  travellersSectionRef,
  isBusinessTraveller,
  customerHasPaid,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentAgent } = useCurrentUser();

  const [offer, setOffer] = useState<App.CustomOffer | undefined>(currentOffer);
  const isExistingOffer = !!currentOffer?.id;
  const [contractFxRates, setContractFxRates] = useState<CurrencyDict | undefined>(undefined);
  const [customerFxRates, setCustomerFxRates] = useState<CurrencyDict | undefined>(undefined);
  const [inclusionsDiscussed, setInclusionsDiscussed] = useState(offer?.status === 'approved');
  const [termsExplained, setTermsExplained] = useState(offer?.status === 'approved');
  const [sendStatus, setSendStatus] = useState<Utils.ProcessingState>('idle');
  const [images, setImages] = useState([]);
  const [formattedImages, setFormattedImages] = useState(offer?.images ?? []);
  const [paymentLinkSent, setPaymentLinkSent] = useState(false);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [paymentLinkSmsSent, setPaymentLinkSmsSent] = useState(false);
  const [sendingPaymentLinkSms, setSendingPaymentLinkSms] = useState(false);
  const { isToggled: deleteDialogOpen, toggleOn: openDeleteDialog, toggleOff: closeDeleteDialog } = useToggleState();
  const { isToggled: imageDialogOpen, toggleOn: openImageDialog, toggleOff: closeImageDialog } = useToggleState();
  const [pendingPropertyImages, setPendingPropertyImages] = useState<Array<PropertyImage>>([]);

  // Fetch the employee (for LEBT only) to get the businessId
  const { data: employee } = useGetEmployee(user.id_member, isBusinessTraveller && !!user.id_member);

  const businessId = employee?.businessId;

  // Fetch the employee list for the business (for LEBT only)
  const { data: employeeListResponse } = useQuery({
    queryKey: ['employeeList', businessId],
    queryFn: () => getEmployeeList(businessId, { page: 0, pageSize: 1000 }),
    enabled: isBusinessTraveller && !!businessId,
  });

  const businessEmployees = useMemo(() => employeeListResponse?.result ?? [], [employeeListResponse]);

  const defaultValues = useMemo(() => {
    const values = buildDefaultValues(brand, offer, user.country_code);

    if (selectedItemType && values.item.type !== selectedItemType) {
      return {
        ...values,
        item: {
          ...values.item,
          type: selectedItemType,
        },
      };
    }

    return values;
  }, [brand, offer, selectedItemType, user.country_code]);

  const customerCurrencies = useMemo(() => getCurrencyCodes(brand).sort((a, b) => a.localeCompare(b)), [brand]);

  const expectedCustomerCurrency = useMemo(() => {
    if (!user.country_code) {
      return null;
    }
    return deriveCustomerCurrency(brand, user.country_code);
  }, [user.country_code, brand]);

  const costPriceCurrencies = useMemo(() => {
    if (contractFxRates) {
      return Object.keys(contractFxRates);
    }

    return [];
  }, [contractFxRates]);

  useEffect(() => {
    async function fetchFxRates() {
      const { customerFxRates, contractFxRates } = await fetchSpotAndContractedRates(
        customerCurrencies,
        enqueueSnackbar,
      );
      setCustomerFxRates(customerFxRates);
      setContractFxRates(contractFxRates);
    }
    fetchFxRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the customer exchange rate once FX rates load for auto-derived non-AUD currencies on new offers
  useEffect(() => {
    if (!isEditMode && customerFxRates && priceCurrencyCode && priceCurrencyCode !== 'AUD') {
      const rate = customerFxRates[priceCurrencyCode];
      if (rate) {
        setValue('payment_metadata.customer_exchange_rate', rate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerFxRates]);

  useEffect(() => {
    const mappedForImagesForm = offer?.images
      ? offer?.images.map((id) => ({
          id: id,
          publicImageId: id,
        }))
      : [];
    setImages(mappedForImagesForm);
    setFormattedImages(offer?.images ?? []);
  }, [offer]);

  const { push, replace } = useAppNavigation();

  const isEditMode = !!offer?.id;

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    watch,
    clearErrors,
    setValue,
  } = useForm<CustomOfferFormValues>({
    defaultValues,
    context: { editMode: isEditMode, brand },
    resolver: yupResolver(hasOrders ? (editableCustomOfferSchema as any) : (customOfferSchema as any)),
    reValidateMode: 'onChange',
    // Default validation happens on onSubmit and all errors are visible at once. The validation will be retriggered on changes for better UX.
  });
  const paymentFlowV2Errors = errors.payment_metadata?.payment_flow_v2 as PaymentFlowV2FieldErrorMap | undefined;

  const { append: addTraveller } = useFieldArray({ control, name: 'travellers' });

  const costPrice = watch('cost_price');
  const costPriceCurrencyCode = watch('cost_price_currency');
  const createdFromBookingNumber = watch('created_from_booking_number');
  const createdFromItemId = watch('created_from_item_id');
  const customerExchangeRate = watch('payment_metadata.customer_exchange_rate');
  const isRebooking = watch('payment_metadata.is_rebooking');
  const itemType = watch('item.type');
  const itemCountry = watch('item.country');
  const supplier = watch('supplier');
  const itemVendor = watch('item.vendor');
  const leServiceFee = watch('payment_metadata.le_service_fee');
  const margin = watch('payment_metadata.margin');
  const paymentTermsGlobal = watch('payment_terms');

  // Legacy (v1) customer payment watchers
  const paymentType = watch('payment_metadata.payment_type');
  const customerDepositAmount = watch('payment_metadata.customer_deposit_1_amount');

  // V2 customer payment watchers
  const depositFlow: PaymentFlowType = watch('payment_metadata.payment_flow_v2.type');
  const v2DepositAmount = watch('payment_metadata.payment_flow_v2.deposit_amount');
  const manualV2DepositAmount = watch('payment_metadata.payment_flow_v2.manual_deposit_amount');
  const v2PaymentDeadline = watch('payment_metadata.payment_flow_v2.payment_deadline');
  const purchaseType = watch('purchase_type') ?? PURCHASE_TYPE_SALES;
  const postPurchaseType = watch('post_purchase_type');

  const endDate = watch('item.end_date');
  const price = watch('price');
  const priceCurrencyCode = watch('price_currency');
  const rebookingFromOrderId = watch('payment_metadata.rebooking_from_order_id');
  const vendorDepositAmount = watch('payment_metadata.vendor_deposit_1_amount');
  const vendorDepositPaymentMade = watch('payment_metadata.vendor_deposit_payment_made');
  const vendorExchangeRate = watch('payment_metadata.vendor_exchange_rate');
  const vendorFinalAmout = watch('payment_metadata.final_amount_vendor');
  const vendorPaymentMade = watch('payment_metadata.vendor_was_paid');
  const vendorPaymentType = watch('payment_metadata.vendor_payment_type');
  const salesAttribution = watch('payment_metadata.sales_attribution');
  const reason = watch('reason');
  const hiddenFromCustomer = watch('hiddenFromCustomer');
  const linkedPackageOfferId = watch('fk_package_custom_offer_id');
  const blockCustomerPaymentNotifications = watch('payment_metadata.block_customer_payment_notifications');
  const priceIncludesGst = watch('payment_metadata.price_includes_gst');
  const cruiselineFee = watch('payment_metadata.cruiseline_fee') ?? 0;
  const cruiselineAdditionalCharges = watch('payment_metadata.cruiseline_additional_charges') ?? 0;
  const cruiseLeFee = watch('payment_metadata.le_fee') ?? 0;
  const refareNewFare = watch('payment_metadata.refare_new_fare') ?? 0;
  const refareNewTaxAndSurcharges = watch('payment_metadata.refare_new_tax_and_surcharges') ?? 0;
  const refareDepositFeeAlreadyPaid = watch('payment_metadata.refare_deposit_fee_already_paid') ?? 0;
  const isCustomOfferPackage = itemType === 'custom-offer-package';
  const offerTypeSelectOptions = useMemo(
    () => getOfferTypeSelectOptions(offerTypes, currentAgent, itemType),
    [offerTypes, currentAgent, itemType],
  );
  const isOfferTypeSelectDisabled = isSubmitting || isOfferTypeSelectLocked(currentAgent, itemType);
  const prevItemTypeRef = useRef(itemType);
  const customerPaymentSectionState = useMemo(
    () =>
      getCustomerPaymentSectionState({
        isExistingOffer,
        paymentFlowType: depositFlow,
        legacyPaymentType: paymentType,
        isCustomOfferPackage,
      }),
    [depositFlow, isExistingOffer, isCustomOfferPackage, paymentType],
  );
  const { showLegacyFinalAmount, showLegacyPaymentTypeSelector, usesLegacyDepositCalculation } =
    customerPaymentSectionState;

  const derivedPaymentFlowV2Amounts = useMemo(
    () =>
      calculatePaymentFlowV2DerivedAmounts({
        paymentFlowType: depositFlow,
        price,
        depositAmount: v2DepositAmount,
        manualDepositAmount: manualV2DepositAmount,
      }),
    [depositFlow, manualV2DepositAmount, price, v2DepositAmount],
  );

  useEffect(() => {
    if (depositFlow === 'manual') {
      setValue(
        'payment_metadata.payment_flow_v2.manual_balance_amount',
        derivedPaymentFlowV2Amounts.manual_balance_amount,
      );
      clearErrors('payment_metadata.payment_flow_v2.manual_balance_amount');
      return;
    }

    if (depositFlow === 'full_payment' || depositFlow === 'payment_schedule') {
      setValue('payment_metadata.payment_flow_v2.balance_amount', derivedPaymentFlowV2Amounts.balance_amount);
      clearErrors('payment_metadata.payment_flow_v2.balance_amount');
    }
  }, [clearErrors, depositFlow, derivedPaymentFlowV2Amounts, setValue]);

  const isItemTypeCruise = isCruiseItemType(itemType);
  const isDepartureCityRequired = isTourOrCruiseItemType(itemType);
  const isCountryRequired = isCountryRequiredItemType(itemType);
  const {
    isCruisePostPurchase,
    isCruiseRefare,
    total: cruisePostPurchaseTotal,
    vendorTotal: cruisePostPurchaseVendorTotal,
  } = useCruisePostPurchase({
    customerPaymentSectionState,
    cruiselineAdditionalCharges: Number(cruiselineAdditionalCharges),
    cruiselineFee: Number(cruiselineFee),
    cruiseLeFee: Number(cruiseLeFee),
    itemType,
    postPurchaseType,
    purchaseType,
    refareDepositFeeAlreadyPaid: Number(refareDepositFeeAlreadyPaid),
    refareNewFare: Number(refareNewFare),
    refareNewTaxAndSurcharges: Number(refareNewTaxAndSurcharges),
    setValue,
    v2PaymentDeadline,
  });
  const fieldLabels = getCustomOfferFieldLabels(isCruisePostPurchase);
  const isActionsDisabled = isSubmitting || sendStatus == 'processing';
  const isSendDisabled = !inclusionsDiscussed || !termsExplained || isActionsDisabled;
  const isAdditionalMarginVisible = salesAttribution === SALES_ATTRIBUTION_MARGIN_MAX_VALUE;
  const shouldLockLeLiveAttribution =
    isEditMode && customerHasPaid && offer?.payment_metadata?.sales_attribution === stores.le_live.utmMedium;

  const showReasonOtherInput = isReasonOtherTextRequired(reason);

  const showSendButton = !!offer?.id && !hasOrders;

  useEffect(() => {
    if (isCustomOfferPackage) {
      setValue('fk_package_custom_offer_id', null);
    }
  }, [isCustomOfferPackage, setValue]);

  const isChangedFromLeLiveOffer = getIsChangedFromLeLiveOffer({
    offer,
    nextItemType: selectedItemType,
  });

  /** LE LIVE stores no real vendor split; once switched away during edit, allow vendor pricing edits despite an order. */
  const lockVendorPriceFieldsForPaidOrder = hasOrders && !isChangedFromLeLiveOffer;

  const onSubmit: SubmitHandler<CustomOfferFormValues> = useCallback(
    async (formData) => {
      try {
        // so far we support only one item
        const payload = buildPayload(user, formData, formattedImages);
        payload.is_changed_from_le_live_offer = isChangedFromLeLiveOffer;
        if (shouldLockLeLiveAttribution) {
          payload.payment_metadata.sales_attribution = stores.le_live.utmMedium;
        }

        const response = offer?.id
          ? await OffersService.updateCustomOffer(offer.id, payload)
          : await OffersService.createCustomOffer(payload);

        const updatedOffer = response.result as App.CustomOffer;

        setOffer(updatedOffer);

        if (onUpdate) {
          await onUpdate(updatedOffer);
        }

        enqueueSnackbar(
          offer?.id ? 'Custom quote has been successfully updated' : 'Custom quote has been successfully created',
          { variant: 'success' },
        );

        if (isRebooking && offer && offer?.payment_metadata?.rebooking_from_order_id !== rebookingFromOrderId) {
          // find the order associated with the custom offer
          const customOrder = await OrdersService.getCustomOrderByCustomOffer(offer.id);
          const relatedOrderId = createdFromBookingNumber ? customOrder.id_orders : null;

          await OrdersService.updateOrderItem(rebookingFromOrderId, createdFromItemId, {
            op: OPERATION_REBOOK_AS_CUSTOM_OFFER,
            fk_rebooked_custom_order_id: relatedOrderId,
          });
          enqueueSnackbar(`Linked LPP order has been updated`, { variant: 'success' });
        }

        replace(`/users/${user.id_member}/custom-offers/${updatedOffer.id}`);
      } catch (err) {
        enqueueSnackbar(`Can't process custom quote: ${err.message}`, { variant: 'error' });
      }
    },
    [
      user,
      formattedImages,
      isChangedFromLeLiveOffer,
      shouldLockLeLiveAttribution,
      offer,
      onUpdate,
      enqueueSnackbar,
      isRebooking,
      rebookingFromOrderId,
      replace,
      createdFromBookingNumber,
      createdFromItemId,
    ],
  );

  const onInvalid: SubmitErrorHandler<CustomOfferFormValues> = useCallback(
    (formErrors) => {
      const validationMessage = getFirstValidationErrorMessage(formErrors);
      enqueueSnackbar(validationMessage ?? 'Please complete the required fields before saving.', { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  const onSendPayment = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!offer?.id) {
      return;
    }

    try {
      setSendStatus('processing');

      const dueDate = offer.due_date;
      if (isBefore(dueDate)) {
        throw new Error('Due date must be in the future');
      }

      const offerResponse = await OffersService.setCustomOfferStatus(offer.id, 'approved');
      const updatedOffer = offerResponse.result as App.CustomOffer;

      setOffer(updatedOffer);

      const isV2Flow = updatedOffer.payment_metadata?.payment_flow_version === 'v2';
      const payload = isV2Flow
        ? await buildCustomOfferPaymentPayloadV2(user, updatedOffer, {
            brand,
            supportManualFlow: true,
            includeBusinessId: true,
          })
        : await buildPaymentPayload(user, updatedOffer, itemType, brand);

      const response = await placeCustomOfferOrder(isBusinessTraveller, user, payload);

      if (isV2Flow && updatedOffer.payment_metadata?.payment_flow_v2?.type === 'manual') {
        void sendManualV2TrackingAlert({
          agentEmail: currentAgent?.email ?? 'unknown',
          orderId: response.result.id_orders,
        });
      }

      if (offer.notes && offer.notes.length > 0) {
        await OrdersService.addOrderNote(response.result.id_orders, {
          comment: offer.notes,
          comment_type: OPERATIONS_SUPPORT,
        });
      }

      enqueueSnackbar('Payment request has been successfully created', { variant: 'success' });

      if (isRebooking) {
        await OrdersService.updateOrderItem(rebookingFromOrderId, createdFromItemId, {
          op: OPERATION_REBOOK_AS_CUSTOM_OFFER,
          fk_rebooked_custom_order_id: response.result.id_orders,
        });
        enqueueSnackbar(`Linked LPP Order has been updated`, { variant: 'success' });
      }
      setSendStatus('success');

      setTimeout(() => window.location.reload(), 200);
    } catch (err) {
      setSendStatus('failed');
      enqueueSnackbar(`Can't create payment request: ${err.message}`, { variant: 'error' });
    }
  };

  const onUpdateImages = (newImageList) => {
    const mappedImages = newImageList.map((image) => image.id);
    return setFormattedImages([...mappedImages]);
  };

  const onAddImage = (cloudinaryId, filename) => {
    const newImage = { id: cloudinaryId, publicImageId: cloudinaryId, title: filename };
    setImages((prev) => [newImage, ...prev]);
    setFormattedImages((prev) => [cloudinaryId, ...prev]);
    return Promise.resolve(newImage);
  };

  const onDeleteImage = async (imageId) => {
    // await deleteImage(imageId);
    setImages((prev) => prev.filter((image) => String(image.id) !== String(imageId)));
    setFormattedImages((prev) => prev.filter((id) => String(id) !== String(imageId)));
    return;
  };

  const deleteOffer = async () => {
    OffersService.deleteCustomOffer(offer.id).then(() => push(`/users/${user.id_member}`));
  };

  const handleSendPaymentLink = useCallback(async () => {
    if (paymentLinkSent || sendingPaymentLink || !order?.id_orders) {
      return;
    }

    try {
      setSendingPaymentLink(true);

      await sendBookingPaymentLink({
        id_orders: order.id_orders,
        brand: brand,
      });

      setPaymentLinkSent(true);
      enqueueSnackbar('Email payment link sent successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to send email payment link: ${error.message}`, { variant: 'error' });
    } finally {
      setSendingPaymentLink(false);
    }
  }, [paymentLinkSent, sendingPaymentLink, order?.id_orders, brand, enqueueSnackbar]);

  const handleSendPaymentLinkSms = useCallback(async () => {
    if (paymentLinkSmsSent || sendingPaymentLinkSms || !order?.id_orders) {
      return;
    }

    try {
      setSendingPaymentLinkSms(true);

      await sendBookingPaymentLinkSms({
        id_orders: order.id_orders,
        brand: brand,
      });

      setPaymentLinkSmsSent(true);
      enqueueSnackbar('SMS payment link sent successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to send SMS payment link: ${error.message}`, { variant: 'error' });
    } finally {
      setSendingPaymentLinkSms(false);
    }
  }, [paymentLinkSmsSent, sendingPaymentLinkSms, order?.id_orders, brand, enqueueSnackbar]);

  const handleCostPriceCurrencyChange = useCallback(
    // cost price == vendor
    (name: string, value: string) => {
      if (contractFxRates) {
        const fieldName = name as keyof CustomOfferFormValues;
        const costPriceCurrencyCode = value;

        const selectCostPriceFxRate = contractFxRates[costPriceCurrencyCode];

        if (!selectCostPriceFxRate || selectCostPriceFxRate === 0) {
          enqueueSnackbar(`Exchange rate not found for ${costPriceCurrencyCode}`, { variant: 'error' });
        }

        setValue('payment_metadata.vendor_exchange_rate', selectCostPriceFxRate ?? 0);

        if (costPriceCurrencyCode === priceCurrencyCode) {
          // ensure same rate is used for equivalent currency codes
          setValue('payment_metadata.customer_exchange_rate', selectCostPriceFxRate ?? 0);
        }
        setValue(fieldName, costPriceCurrencyCode);
      }
    },
    [priceCurrencyCode, contractFxRates, setValue, enqueueSnackbar],
  );

  // This can be further improved by extracting into shared CustomOfferTravellersSection.tsx and reimplementing the populate form logic
  const handleAddNewTraveller = useCallback(
    (type = 'adult') => {
      if (isBusinessTraveller) {
        addTraveller({
          name: '',
          first_name: '',
          last_name: '',
          email: '',
          type: type,
          country: '',
          date_of_birth: null,
          employee: null,
        } as App.CustomOfferTraveller);
      } else {
        addTraveller({
          name: '',
          type: type,
          country: '',
          date_of_birth: null,
        } as App.CustomOfferTraveller);
      }
    },
    [addTraveller, isBusinessTraveller],
  );

  const handlePriceCurrencyChange = useCallback(
    // customer
    (name: string, value: string) => {
      if (customerFxRates) {
        const fieldName = name as keyof CustomOfferFormValues;
        const priceCurrencyCode = value;
        const selectPriceFxRate = customerFxRates[priceCurrencyCode];

        if (!selectPriceFxRate || selectPriceFxRate === 0) {
          enqueueSnackbar(`Exchange rate not found for ${priceCurrencyCode}`, { variant: 'error' });
        }

        if (costPriceCurrencyCode === priceCurrencyCode) {
          // hardcode this to the same rate as vendor
          setValue('payment_metadata.customer_exchange_rate', vendorExchangeRate);
        } else {
          setValue('payment_metadata.customer_exchange_rate', selectPriceFxRate ?? 0);
        }

        setValue(fieldName, priceCurrencyCode);

        if (priceCurrencyCode === 'AUD') {
          setValue('payment_metadata.le_entity', LE_ENTITY_AUS);
        } else {
          setValue('payment_metadata.le_entity', LE_ENTITY_INTL);
        }
      }
    },
    [costPriceCurrencyCode, vendorExchangeRate, customerFxRates, setValue, enqueueSnackbar],
  );

  const calculateVendorCostTotal = useCallback(() => {
    return Number(vendorDepositAmount) + Number(vendorFinalAmout);
  }, [vendorDepositAmount, vendorFinalAmout]);

  const calculateVendorCostTotalWithMargin = useCallback(() => {
    return Number(vendorDepositAmount) + Number(vendorFinalAmout) + Number(margin) + Number(leServiceFee);
  }, [leServiceFee, margin, vendorDepositAmount, vendorFinalAmout]);

  const calculateVendorTotalFromCostPrice = useCallback(() => {
    return Number(costPrice) - (Number(margin) + Number(leServiceFee));
  }, [costPrice, leServiceFee, margin]);

  const calculateVendorFinalPaymentAmount = useCallback(() => {
    return Number(costPrice) - Number(vendorDepositAmount);
  }, [costPrice, vendorDepositAmount]);

  // We need to Calculate the vendor cost total for the form.
  const calculateCustomerPriceTotalAndCurrencyConvert = useCallback(() => {
    return calculateCustomerSellPriceWithFx({
      costPrice,
      margin,
      serviceFee: leServiceFee,
      customerExchangeRate,
      vendorExchangeRate,
    });
  }, [costPrice, customerExchangeRate, leServiceFee, margin, vendorExchangeRate]);

  const calculateCustomerFinalPaymentAmount = useCallback(() => {
    return calculateLegacyCustomerFinalPaymentAmount({
      price,
      customerDepositAmount,
      usesLegacyDepositCalculation,
    });
  }, [customerDepositAmount, price, usesLegacyDepositCalculation]);

  // VCC is non-editable once created. Hence the fields used to create the VCC should be disabled.
  const disableVendorVccFieldsOnceVccIsCreated = useMemo(() => {
    return paymentTermsGlobal === 'vcc' && (offer?.has_vcc || false);
  }, [offer?.has_vcc, paymentTermsGlobal]);

  const paramsForVccForCustomOffer = useMemo(() => {
    return {
      hasVCC: offer?.has_vcc || false,
      paymentTermsGlobal: paymentTermsGlobal as 'eft' | 'vcc',
      customOfferId: offer?.id || null,
      customOfferStatus: offer?.status || 'draft',
      currencyCode: costPriceCurrencyCode || null,
      costPrice: costPrice || 0,
      guestName: offer?.customer_full_name ?? '',
      validityTo: endDate ? formatDateISO(endDate) : null,
      saveCustomOffer: () => handleSubmit(onSubmit, onInvalid)(),
    };
  }, [
    offer?.has_vcc,
    offer?.id,
    offer?.status,
    offer?.customer_full_name,
    paymentTermsGlobal,
    costPriceCurrencyCode,
    costPrice,
    endDate,
    handleSubmit,
    onSubmit,
    onInvalid,
  ]);

  // Update the vendor cost price when the any of the following fields are changed.
  // vendor_deposit_1_amount | final_amount_vendor | margin | le_service_fee
  useEffect(() => {
    if (!lockVendorPriceFieldsForPaidOrder) {
      setValue('cost_price', isCruisePostPurchase ? cruisePostPurchaseVendorTotal : calculateVendorCostTotal());
      if (isCruisePostPurchase) {
        setValue('payment_metadata.final_amount_vendor', cruisePostPurchaseVendorTotal);
      }
      clearErrors('cost_price');
    }
  }, [
    lockVendorPriceFieldsForPaidOrder,
    setValue,
    calculateVendorCostTotal,
    clearErrors,
    cruisePostPurchaseVendorTotal,
    isCruisePostPurchase,
  ]);

  // If the finance team need to amend the vendor payment terms after the order has been created.  I.e. swap to deposit.
  // Only update the vendor final amount without affecting any other values on the form.
  useEffect(() => {
    if (lockVendorPriceFieldsForPaidOrder) {
      setValue('payment_metadata.final_amount_vendor', calculateVendorFinalPaymentAmount());
      clearErrors('payment_metadata.final_amount_vendor');
    }
  }, [calculateVendorFinalPaymentAmount, clearErrors, lockVendorPriceFieldsForPaidOrder, setValue]);

  // Update the customer price when the vendor cost price is changed.
  useEffect(() => {
    if (!hasOrders) {
      setValue(
        'price',
        isCruisePostPurchase ? cruisePostPurchaseTotal : calculateCustomerPriceTotalAndCurrencyConvert(),
      );
      clearErrors('price');
    }
  }, [
    setValue,
    hasOrders,
    calculateCustomerPriceTotalAndCurrencyConvert,
    clearErrors,
    cruisePostPurchaseTotal,
    isCruisePostPurchase,
  ]);

  // update the customer final payment amount
  useEffect(() => {
    if (!showLegacyFinalAmount) {
      return;
    }

    setValue('payment_metadata.final_amount_customer', calculateCustomerFinalPaymentAmount());
    clearErrors('payment_metadata.final_amount_customer');
  }, [calculateCustomerFinalPaymentAmount, clearErrors, setValue, showLegacyFinalAmount]);

  // update the vendor payment amounts if the payment type is changed.
  useEffect(() => {
    if (vendorPaymentType !== 'deposit' && vendorDepositAmount !== 0) {
      if (lockVendorPriceFieldsForPaidOrder) {
        setValue('payment_metadata.final_amount_vendor', calculateVendorTotalFromCostPrice());
        clearErrors('payment_metadata.final_amount_vendor');
      }
      setValue('payment_metadata.vendor_deposit_1_amount', 0);
      clearErrors('payment_metadata.vendor_deposit_1_amount');
    }
  }, [
    calculateVendorTotalFromCostPrice,
    clearErrors,
    lockVendorPriceFieldsForPaidOrder,
    setValue,
    vendorDepositAmount,
    vendorPaymentType,
  ]);

  // reset financials when custom-offer-package is selected, and revert when switching away
  useEffect(() => {
    const prevItemType = prevItemTypeRef.current;

    if (itemType === 'custom-offer-package') {
      setValue('cost_price', 0);
      setValue('price', 0);
      setValue('payment_metadata.margin', 0);
      setValue('payment_metadata.le_service_fee', 0);
      setValue('payment_metadata.final_amount_vendor', 0);
      setValue('payment_metadata.final_amount_customer', 0);
      setValue('payment_metadata.payment_type', 'full_payment');
      setValue('payment_metadata.payment_flow_v2.type', undefined);
      setValue('payment_metadata.block_customer_payment_notifications', true);
      setValue('reason', 'other');
      setValue('reasonOther', 'Content only offer');
      setValue('hiddenFromCustomer', false);
      clearErrors([
        'cost_price',
        'price',
        'payment_metadata.margin',
        'payment_metadata.le_service_fee',
        'payment_metadata.final_amount_vendor',
        'payment_metadata.final_amount_customer',
        'payment_metadata.payment_type',
        'payment_metadata.payment_flow_v2',
        'reason',
        'reasonOther',
        'hiddenFromCustomer',
      ]);
    } else if (prevItemType === 'custom-offer-package') {
      setValue('payment_metadata.payment_type', isBusinessTraveller ? 'full_payment' : 'deposit');
      setValue('payment_metadata.block_customer_payment_notifications', false);
      setValue('reason', '');
      setValue('reasonOther', '');
    }

    prevItemTypeRef.current = itemType;
  }, [itemType, isBusinessTraveller, setValue, clearErrors]);

  // Only for safety. This will prevent the customer emails on successful payment.
  // If the custom offer is hidden, we don't want customer to receive any emails that includes pricing information.
  useEffect(() => {
    if ((hiddenFromCustomer || linkedPackageOfferId) && !blockCustomerPaymentNotifications) {
      setValue('payment_metadata.block_customer_payment_notifications', true);
    }
  }, [blockCustomerPaymentNotifications, hiddenFromCustomer, linkedPackageOfferId, setValue]);

  // reset the customer deposit amount if the payment type is changed.
  useEffect(() => {
    if (!showLegacyPaymentTypeSelector) {
      return;
    }

    if (!usesLegacyDepositCalculation) {
      setValue('payment_metadata.customer_deposit_1_amount', 0);
      clearErrors('payment_metadata.customer_deposit_1_amount');
    }
  }, [clearErrors, setValue, showLegacyPaymentTypeSelector, usesLegacyDepositCalculation]);

  // Calculate GST amount based on price and checkbox
  useEffect(() => {
    const gstAmount = priceIncludesGst ? (Number(price) || 0) / 11 : 0;
    setValue('payment_metadata.gst_amount', gstAmount);
    clearErrors('payment_metadata.gst_amount');
  }, [priceIncludesGst, price, setValue, clearErrors]);

  // Business Traveller Hotel: tick "Price includes GST" only when country is Australia (case insensitive)
  useEffect(() => {
    if (!isBusinessTraveller || itemType !== 'hotel') {
      return;
    }
    const isAustralia = (itemCountry ?? '').trim().toLowerCase() === 'australia';
    setValue('payment_metadata.price_includes_gst', isAustralia);
  }, [isBusinessTraveller, itemType, itemCountry, setValue]);

  const handleOfferTypeChange = useCallback(
    (event: SelectChangeEvent<App.CustomOfferType>) => {
      const nextType = event.target.value as App.CustomOfferType;
      const currentInternalDiscountTracking = watch('payment_metadata.internal_discount_tracking');
      const currentVendorName = watch('item.vendor')?.trim() || '';
      setValue('item.type', nextType);
      setValue('supplier', 'Other');
      changeFormType(nextType, currentInternalDiscountTracking, currentVendorName);
    },
    [setValue, watch, changeFormType],
  );

  const handleSupplierChange = useCallback(
    (_, value: string) => {
      setValue('supplier', value);
      setValue('item.vendor', value === 'Other' ? '' : value);
    },
    [setValue],
  );

  const applyPropertyImages = useCallback((propertyImages: Array<PropertyImage>, mode: 'append' | 'replace') => {
    const mappedImages = propertyImages.map((img) => ({
      id: img.id_cloudinary_external,
      publicImageId: img.id_cloudinary_external,
      title: img.title || img.filename || '',
    }));
    const mappedIds = propertyImages.map((img) => img.id_cloudinary_external);

    if (mode === 'replace') {
      setImages(mappedImages);
      setFormattedImages(mappedIds);
    } else {
      setImages((prev) => [...prev, ...mappedImages]);
      setFormattedImages((prev) => [...prev, ...mappedIds]);
    }
  }, []);

  const handleImageDialogAction = useCallback(
    (mode: 'append' | 'replace') => {
      applyPropertyImages(pendingPropertyImages, mode);
      setPendingPropertyImages([]);
      closeImageDialog();
    },
    [pendingPropertyImages, applyPropertyImages, closeImageDialog],
  );

  const handlePropertyImagePrefill = useCallback(
    (propertyImages: Array<PropertyImage>) => {
      if (propertyImages.length > 0) {
        if (images.length > 0 || formattedImages.length > 0) {
          // If the offer already has images, prompt the user to choose whether to replace or append.
          setPendingPropertyImages(propertyImages);
          openImageDialog();
        } else {
          applyPropertyImages(propertyImages, 'append');
        }
      }
    },
    [applyPropertyImages, openImageDialog, images, formattedImages],
  );

  const handlePropertySelect = useCallback(
    (property: Property | null) => {
      if (property) {
        setValue('item.name', property.name);
        setValue('item.city', property.geo_data?.locality || '');
        setValue('item.country', property.geo_data?.country || '');
        handlePropertyImagePrefill(property.images ?? []);
      }
    },
    [setValue, handlePropertyImagePrefill],
  );

  const handleOfferNameChange = useCallback(
    (value: string) => {
      setValue('item.name', value);
    },
    [setValue],
  );

  const offerTypeSuppliers = useMemo(() => {
    return offerTypes.find((offerType) => offerType.value === itemType)?.suppliers || ['Other'];
  }, [offerTypes, itemType]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit, onInvalid)} data-testid="custom-offer-form">
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={4}>
          <Typography variant="h6" fontWeight="normal" color="GrayText">
            Customer: {user.fullName}
          </Typography>
          <Typography variant="h6" fontWeight="normal" color="GrayText">
            Email: {user.email}
          </Typography>
        </Stack>
        {itemType === 'hotel' && (
          <Stack direction="row" spacing={2}>
            <PrePopulateCustomOfferForm
              sendStatus={sendStatus}
              setSendStatus={setSendStatus}
              setValue={setValue}
              watch={watch}
              user={user}
              control={control}
              isSubmitting={isSubmitting}
              handleAddNewTraveller={handleAddNewTraveller}
            />
          </Stack>
        )}
      </Stack>
      <Box mt={2}>
        <Typography variant="h6">General offer information</Typography>
        <CustomOfferRequiredFieldsLegend />
      </Box>
      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.type"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <InputLabel required>Offer type</InputLabel>
                <Select
                  {...field}
                  label="Offer type"
                  disabled={isOfferTypeSelectDisabled}
                  onChange={handleOfferTypeChange}
                  data-testid="offer-type"
                >
                  {offerTypeSelectOptions.map((offerType) => (
                    <MenuItem key={offerType.value} value={offerType.value}>
                      {offerType.label}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>

        {isItemTypeCruise && (
          <CruisePostPurchaseTypeFields
            control={control}
            cruiseLeFee={Number(cruiseLeFee)}
            disabled={isSubmitting || lockVendorPriceFieldsForPaidOrder}
            isEditMode={isEditMode}
            purchaseType={purchaseType}
            setValue={setValue}
          />
        )}

        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="supplier"
            control={control}
            render={({ field, fieldState }) => (
              <FormControl error={fieldState.invalid} fullWidth>
                <Autocomplete
                  {...field}
                  options={offerTypeSuppliers}
                  disabled={isSubmitting}
                  autoHighlight
                  renderInput={(params) => <TextField {...params} label="Select supplier" required />}
                  onChange={handleSupplierChange}
                  // For existing offers, set initial value based on vendor
                  value={isEditMode && itemVendor && offerTypeSuppliers.includes(itemVendor) ? itemVendor : field.value}
                />
                <FormHelperText>{fieldState.error?.message}</FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        <Grid hidden={supplier !== 'Other' || offerTypeSuppliers.includes(itemVendor)} size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.vendor"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Supplier (Other)"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                fullWidth
                data-testid="supplier"
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.salesforce_case_number"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={fieldLabels.ticketNumber}
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                fullWidth
                data-testid="salesforce-case-number"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.vendor_reference_number"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={fieldLabels.vendorReference}
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                fullWidth
                data-testid="supplier-booking-reference"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="sold_at"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                label={fieldLabels.salesDate}
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting || hasOrders}
                fullWidth
                data-testid="sales-date"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="payment_metadata.sales_agent_name"
            control={control}
            rules={{ required: `${fieldLabels.agent} is required` }}
            render={({ field, fieldState: { error } }) => (
              <WholesaleAgentAutocomplete
                value={field.value || ''}
                onChange={field.onChange}
                required
                error={!!error}
                helperText={error?.message}
                disabled={isSubmitting}
                label={fieldLabels.agent}
                options={isCruisePostPurchase ? CONCIERGE_AGENT_LIST : WHOLESALE_AGENT_LIST}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="payment_metadata.sales_attribution"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Sales Attribution"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting || shouldLockLeLiveAttribution}
                select
                fullWidth
                InputLabelProps={{ shrink: !!field.value }}
                data-testid="sales-attribution"
              >
                {getSalesAttributionList(field.value).map((attributionType) => (
                  <MenuItem
                    key={attributionType.value}
                    value={attributionType.value}
                    disabled={!attributionType.enabled}
                  >
                    {attributionType.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        {isCruisePostPurchase && (
          <Grid size={{ xs: 3, md: 1 }}>
            <Controller
              name="created_from_booking_number"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Related LE Booking Number"
                  helperText={error?.message}
                  error={!!error}
                  disabled={isSubmitting}
                  fullWidth
                  data-testid="related-le-booking-number"
                />
              )}
            />
          </Grid>
        )}
        {!isCruisePostPurchase && (
          <Grid size={{ xs: 6, md: 2 }}>
            <Stack direction="row" spacing={1}>
              <Box sx={{ width: 120, flexShrink: 0 }}>
                <Controller
                  name="customer_phone_prefix"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Autocomplete
                      {...field}
                      value={phonePrefixOptions.find((opt) => opt.value === value) ?? null}
                      onChange={(_, selected) => onChange(selected?.value ?? '')}
                      options={phonePrefixOptions}
                      getOptionLabel={(option) => `+${option.value}`}
                      renderOption={(props, option) => <li {...props}>{option.label}</li>}
                      filterOptions={(options, { inputValue }) => {
                        const input = inputValue.toLowerCase();
                        return options.filter((opt) => opt.label.toLowerCase().includes(input));
                      }}
                      slotProps={{ popper: { style: { minWidth: 280 }, placement: 'bottom-end' } }}
                      autoHighlight
                      disabled={isSubmitting}
                      renderInput={(params) => <TextField {...params} label="Prefix" />}
                      data-testid="customer-phone-prefix"
                    />
                  )}
                />
              </Box>
              <Controller
                name="customer_phone_number"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Phone Number"
                    helperText={error?.message}
                    error={!!error}
                    disabled={isSubmitting}
                    fullWidth
                    inputProps={{ maxLength: 15 }}
                    data-testid="customer-phone-number"
                  />
                )}
              />
            </Stack>
          </Grid>
        )}
      </Grid>
      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={isAdditionalMarginVisible ? 5 : 6}>
          <PropertyAutocomplete
            value={watch('item.name') || ''}
            onPropertySelect={handlePropertySelect}
            onInputChange={handleOfferNameChange}
            error={!!errors.item?.name}
            helperText={
              errors.item?.name?.message ||
              'Search for a property or enter a custom name. This is a customer-facing field.'
            }
            label="Offer name"
            required
            disabled={isSubmitting}
          />
        </Grid>
        {isAdditionalMarginVisible && (
          <Grid size={1}>
            <TextField
              {...register('payment_metadata.additional_margin')}
              label="Additional margin"
              error={!!errors.payment_metadata?.additional_margin}
              helperText={errors.payment_metadata?.additional_margin?.message}
              disabled={isSubmitting}
              type="number"
              inputProps={{ step: 0.01 }}
              fullWidth
              data-testid="additional-margin"
            />
          </Grid>
        )}
      </Grid>
      {!isCruisePostPurchase && (
        <Grid mt={2} columns={6} spacing={2} container>
          <Grid size={{ xs: 3, md: 1 }}>
            <TextField
              {...register('item.city')}
              label={isDepartureCityRequired ? 'Departure City' : 'City'}
              required={isDepartureCityRequired}
              helperText={errors.item?.city?.message}
              error={!!errors.item?.city}
              InputLabelProps={{ shrink: true }}
              disabled={isSubmitting}
              fullWidth
              data-testid="city"
            />
          </Grid>
          <Grid size={{ xs: 3, md: 1 }}>
            {isBusinessTraveller && itemType === 'hotel' ? (
              <Controller
                name="item.country"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    value={field.value || null}
                    onChange={(_, newValue) => field.onChange(newValue ?? '')}
                    onBlur={field.onBlur}
                    options={countries}
                    disabled={isSubmitting}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputRef={field.ref}
                        label="Country"
                        required
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputLabelProps={{ shrink: true }}
                        data-testid="country"
                      />
                    )}
                    data-testid="country-autocomplete"
                  />
                )}
              />
            ) : (
              <TextField
                {...register('item.country')}
                label="Country"
                required={isCountryRequired}
                helperText={errors.item?.country?.message}
                error={!!errors.item?.country}
                InputLabelProps={{ shrink: true }}
                disabled={isSubmitting}
                fullWidth
                data-testid="country"
              />
            )}
          </Grid>
        </Grid>
      )}
      {isCruisePostPurchase && (
        <CruisePostPurchaseCoreDetails
          control={control}
          disableVendorVccFieldsOnceVccIsCreated={disableVendorVccFieldsOnceVccIsCreated}
          isSubmitting={isSubmitting}
        />
      )}
      <Grid mt={2} columns={4} spacing={2} container>
        {!isCruisePostPurchase && (
          <Grid size={{ xs: 3, md: 0.5 }}>
            <Controller
              name="item.start_date"
              control={control}
              render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                <>
                  <DateWidget
                    {...field}
                    label="Start date"
                    slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                    disabled={isSubmitting || disableVendorVccFieldsOnceVccIsCreated}
                    fullWidth
                    data-testid="start-date"
                  />
                  {disableVendorVccFieldsOnceVccIsCreated && <VccCreatedDisableFieldHelperText />}
                </>
              )}
            />
          </Grid>
        )}
        {!isCruisePostPurchase && (
          <>
            <Grid size={{ xs: 3, md: 0.5 }}>
              <Controller
                name="item.end_date"
                control={control}
                render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                  <>
                    <DateWidget
                      {...field}
                      slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                      disabled={isSubmitting || disableVendorVccFieldsOnceVccIsCreated}
                      label="End date"
                      minDate={watch(`item.start_date`) ? newDate(watch(`item.start_date`)) : undefined}
                      data-testid="end-date"
                    />
                    {disableVendorVccFieldsOnceVccIsCreated && <VccCreatedDisableFieldHelperText />}
                  </>
                )}
              />
            </Grid>

            <Grid size={{ xs: 3, md: 1 }}>
              <Controller
                name="reason"
                control={control}
                render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Reason for custom offer"
                    required
                    helperText={error?.message}
                    error={!!error}
                    disabled={isSubmitting}
                    select
                    fullWidth
                    data-testid="reason-for-custom-offer"
                  >
                    {REASONS.map((reason, index) => (
                      <MenuItem value={reason.value} key={index}>
                        {reason.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            {showReasonOtherInput && (
              <Grid size={2}>
                <Controller
                  name="reasonOther"
                  control={control}
                  render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Reason for custom offer - Other"
                      required
                      helperText={error?.message}
                      error={!!error}
                      disabled={isSubmitting}
                      fullWidth
                      data-testid="reason-for-custom-offer-other"
                    />
                  )}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Grid mt={2} columns={4} spacing={2} container>
        {isItemTypeCruise && !isCruisePostPurchase && (
          <>
            <Grid size={{ xs: 4, md: 1 }}>
              <TextField
                {...register('item.cruise_line')}
                helperText={errors.item?.cruise_line?.message}
                error={!!errors.item?.cruise_line}
                disabled={isSubmitting}
                label="Cruise Line"
                required
                fullWidth
                data-testid="cruise-line"
              />
            </Grid>

            {!isCruisePostPurchase && (
              <>
                <Grid size={{ xs: 4, md: 1 }}>
                  <TextField
                    {...register('item.ship_name')}
                    helperText={errors.item?.ship_name?.message}
                    error={!!errors.item?.ship_name}
                    disabled={isSubmitting}
                    label="Ship"
                    required
                    fullWidth
                    data-testid="ship-name"
                  />
                </Grid>

                <Grid size={{ xs: 4, md: 1 }}>
                  <TextField
                    {...register('item.ship_cabin')}
                    helperText={errors.item?.ship_cabin?.message}
                    error={!!errors.item?.ship_cabin}
                    disabled={isSubmitting}
                    label="Cabin"
                    required
                    fullWidth
                    data-testid="ship-cabin"
                  />
                </Grid>

                <Grid size={{ xs: 4, md: 1 }}>
                  <TextField
                    {...register('item.ship_rate')}
                    helperText={errors.item?.ship_rate?.message}
                    error={!!errors.item?.ship_rate}
                    disabled={isSubmitting}
                    label="Rate"
                    required
                    fullWidth
                    data-testid="ship-rate"
                  />
                </Grid>

                <Grid size={{ xs: 4, md: 2 }}>
                  <TextField
                    {...register('payment_metadata.reason_additional_info')}
                    error={!!errors.payment_metadata?.reason_additional_info}
                    disabled={isSubmitting}
                    label="Additional information"
                    helperText={
                      errors.payment_metadata?.reason_additional_info
                        ? errors.payment_metadata?.reason_additional_info.message
                        : 'Provide 1-2 additional sentences on why custom offer is required'
                    }
                    fullWidth
                    data-testid="reason-additional-info"
                  />
                </Grid>

                <Grid size={{ xs: 4, md: 2 }}>
                  <Controller
                    name="payment_metadata.reason_issue_raised"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} data-testid="reason-issue-raised" />}
                        label="Was this issue raised internally? (007/Connectivity)"
                      />
                    )}
                  />
                </Grid>
              </>
            )}
          </>
        )}

        <Grid size={4}>
          <RichTextEditor
            {...register('item.description')}
            control={control}
            label={isItemTypeCruise ? 'Key information' : 'Description'}
            required
            helperText={
              errors.item?.description
                ? errors.item.description.message
                : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            error={!!errors.item?.description}
            disabled={isSubmitting}
            data-testid="description"
          />
        </Grid>
        <Grid size={4}>
          <RichTextEditor
            {...register('item.service_information')}
            control={control}
            label="Terms & Conditions"
            required
            helperText={
              errors.item?.service_information
                ? errors.item.service_information.message
                : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            error={!!errors.item?.service_information}
            disabled={isSubmitting}
            data-testid="terms-and-conditions"
          />
        </Grid>
        {itemType === 'hotel' && (
          <CancellationPolicies control={control} watch={watch} isSubmitting={isSubmitting} setValue={setValue} />
        )}
      </Grid>
      <TravellersSection
        ref={travellersSectionRef}
        control={control}
        register={register}
        errors={errors}
        setValue={setValue}
        isSubmitting={isSubmitting}
        isBusinessTraveller={isBusinessTraveller}
        isEditMode={isEditMode}
        businessEmployees={businessEmployees}
        customerHasPaid={customerHasPaid}
        itemType={itemType}
      />
      {!isCustomOfferPackage && (
        <>
          <Box mt={2} hidden={isCruisePostPurchase}>
            <Typography variant="h6">Vendor payment information</Typography>
          </Box>
          <Grid mt={2} container columns={12} spacing={2} sx={{ display: isCruisePostPurchase ? 'none' : 'flex' }}>
            <Grid size={{ xs: 12, md: 8 }}>
              {/* 8 columns, 3 rows */}
              <Grid container columns={12} spacing={2}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name="payment_metadata.vendor_payment_type"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Payment type"
                        disabled={isSubmitting}
                        select
                        fullWidth
                        helperText={errors.payment_metadata?.vendor_payment_type?.message}
                        error={!!errors?.payment_metadata?.vendor_payment_type}
                        data-testid="vendor-payment-type"
                      >
                        <MenuItem value="deposit">Deposit</MenuItem>
                        <MenuItem value="full_payment">Full Payment</MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name="payment_terms"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Payment terms"
                        disabled={isSubmitting}
                        select
                        fullWidth
                        data-testid="vendor-payment-terms"
                      >
                        <MenuItem value="vcc">VCC</MenuItem>
                        <MenuItem value="eft">EFT</MenuItem>
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name="cost_price_currency"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FormControl error={fieldState.invalid} fullWidth>
                        <Autocomplete
                          {...field}
                          options={costPriceCurrencies}
                          autoHighlight
                          disableClearable
                          renderInput={(params) => <TextField {...params} label="Vendor Currency" />}
                          onChange={(_, data) => {
                            handleCostPriceCurrencyChange(field.name, data);
                          }}
                          disabled={disableVendorVccFieldsOnceVccIsCreated}
                          data-testid="vendor-cost-price-currency"
                        />
                        {fieldState.error?.message && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                  {disableVendorVccFieldsOnceVccIsCreated && <VccCreatedDisableFieldHelperText />}
                </Grid>
                <Grid container direction="row" alignItems="center" size={{ xs: 12, md: 3 }}>
                  <Typography variant="subtitle2">
                    AUD to {costPriceCurrencyCode} Fx Rate = {vendorExchangeRate}
                  </Typography>
                  <HtmlTooltip
                    title={
                      <RatesTable
                        vendorCurrency={costPriceCurrencyCode}
                        customerCurrency={priceCurrencyCode}
                        customerFxRates={customerFxRates}
                        contractFxRates={contractFxRates}
                      />
                    }
                    placement="right"
                  >
                    <IconButton>
                      <HelpIcon />
                    </IconButton>
                  </HtmlTooltip>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Controller
                    name="payment_metadata.vendor_is_non_refundable"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={!!field.value} data-testid="vendor-non-refundable" />}
                        label="Vendor payment is non-refundable (delay booking confirmation)"
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {vendorPaymentType === 'deposit' && (
                <Grid mt={2} container columns={12} spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      {...register('payment_metadata.vendor_deposit_1_amount')}
                      helperText={errors.payment_metadata?.vendor_deposit_1_amount?.message}
                      error={!!errors.payment_metadata?.vendor_deposit_1_amount}
                      disabled={isSubmitting}
                      label="Deposit amount (vendor)"
                      type="number"
                      inputProps={{ step: 0.01 }}
                      fullWidth
                      data-testid="vendor-deposit-amount"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="payment_metadata.vendor_deposit_1_due_date"
                      control={control}
                      render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                        <DateWidget
                          {...field}
                          slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                          disabled={isSubmitting}
                          label="Deposit 1 due date (vendor)"
                          data-testid="vendor-deposit-due-date"
                        />
                      )}
                    />
                  </Grid>
                  {paymentTermsGlobal == 'vcc' && (
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Controller
                        name="payment_metadata.deposit_vcc_digits"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            helperText={error?.message}
                            error={!!error}
                            disabled={isSubmitting}
                            label="Deposit VCC 4 digits field"
                            fullWidth
                            type="text"
                            onChange={(event) => {
                              const { value } = event.target;
                              const onlyNums = value.replace(/[^0-9]/g, '');
                              if (onlyNums.length < 5) {
                                field.onChange(onlyNums);
                              }
                            }}
                            data-testid="deposit-vcc-digits"
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="payment_metadata.vendor_deposit_payment_made"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox {...field} checked={field.value} data-testid="vendor-deposit-payment-made" />
                          }
                          label="Deposit payment made"
                        />
                      )}
                    />
                  </Grid>
                  {vendorDepositPaymentMade && (
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Controller
                        name="payment_metadata.vendor_deposit_payment_date"
                        control={control}
                        render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                          <DateWidget
                            {...field}
                            slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                            disabled={isSubmitting}
                            label="Deposit payment date"
                            data-testid="vendor-deposit-payment-date"
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              )}

              <Grid mt={2} container columns={12} spacing={2}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    {...register('payment_metadata.final_amount_vendor')}
                    helperText={errors?.payment_metadata?.final_amount_vendor?.message}
                    error={!!errors?.payment_metadata?.final_amount_vendor}
                    disabled={lockVendorPriceFieldsForPaidOrder || isSubmitting}
                    label="Final amount (vendor)"
                    type="number"
                    inputProps={{ step: 0.01 }}
                    fullWidth
                    required
                    data-testid="vendor-final-amount"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name="payment_metadata.vendor_due_date"
                    control={control}
                    render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                      <DateWidget
                        {...field}
                        slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                        disabled={isSubmitting}
                        label="Payment due date (vendor)"
                        disablePast
                        data-testid="vendor-payment-due-date"
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name="payment_metadata.vendor_was_paid"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox {...field} checked={field.value} data-testid="vendor-payment-made" />}
                        label="Vendor payment made"
                      />
                    )}
                  />
                </Grid>
                {vendorPaymentMade && (
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Controller
                      name="payment_metadata.vendor_payment_date"
                      control={control}
                      render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
                        <DateWidget
                          {...field}
                          slotProps={{ textField: { helperText: error?.message, error: !!error } }}
                          disabled={isSubmitting}
                          label="Vendor payment date"
                          data-testid="vendor-payment-date"
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Box
            my={2}
            display={
              isCruisePostPurchase || (paymentTermsGlobal === 'eft' && !paramsForVccForCustomOffer.hasVCC)
                ? 'none'
                : 'block'
            }
          >
            <CustomOfferVendorVirtualCreditCardSectionHeading
              paymentTermsGlobal={paramsForVccForCustomOffer.paymentTermsGlobal}
              hasVCC={paramsForVccForCustomOffer.hasVCC}
            />
            <CustomOfferVendorVirtualCreditCardSection paramsForVccForCustomOffer={paramsForVccForCustomOffer} />
          </Box>
          <Box mt={2} hidden={isCruisePostPurchase}>
            <Typography variant="h6">Vendor Offer Prices - {costPriceCurrencyCode}</Typography>
          </Box>
          <Grid mt={2} container columns={6} spacing={2} sx={{ display: isCruisePostPurchase ? 'none' : 'flex' }}>
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                {...register('cost_price')}
                helperText={errors.cost_price?.message}
                error={!!errors.cost_price}
                disabled
                InputLabelProps={{ shrink: true }}
                label="Cost price"
                type="number"
                fullWidth
                data-testid="cost-price"
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                {...register('payment_metadata.margin')}
                helperText={errors.payment_metadata?.margin?.message}
                error={!!errors.payment_metadata?.margin}
                label="Margin"
                disabled={lockVendorPriceFieldsForPaidOrder || isSubmitting}
                type="number"
                inputProps={{ step: 0.01 }}
                defaultValue={190}
                InputLabelProps={{ shrink: true }}
                fullWidth
                data-testid="margin"
              />
            </Grid>
            {!isBusinessTraveller && (
              <Grid size={{ xs: 3, md: 1 }}>
                <TextField
                  {...register('payment_metadata.le_service_fee')}
                  helperText={errors.payment_metadata?.le_service_fee?.message}
                  error={!!errors.payment_metadata?.le_service_fee}
                  disabled={lockVendorPriceFieldsForPaidOrder || isSubmitting}
                  label="Le service fee"
                  type="number"
                  inputProps={{ step: 0.01 }}
                  fullWidth
                  data-testid="le-service-fee"
                />
              </Grid>
            )}
            <Grid container direction="row" alignItems="center" size={{ xs: 3, md: 1 }}>
              <Box>
                <Typography variant="subtitle2">
                  Sell Price ({costPriceCurrencyCode}): {calculateVendorCostTotalWithMargin()}
                </Typography>
              </Box>
            </Grid>
            <Grid container direction="row" alignItems="center" size={{ xs: 6, md: 2 }}>
              <Box>
                <CustomOfferLoyaltyCalculator
                  productType={selectedItemType}
                  sellPrice={Number(calculateVendorCostTotalWithMargin())}
                  currency={costPriceCurrencyCode}
                  margin={Number(margin)}
                  leServiceFee={Number(leServiceFee) || 0}
                  userId={user?.id_member}
                  salesAttribution={salesAttribution}
                  brand={brand}
                />
              </Box>
            </Grid>
          </Grid>
          <Box mt={2} hidden={isCruisePostPurchase}>
            <Typography variant="h6">Customer Offer Price - {priceCurrencyCode}</Typography>
          </Box>
          <Grid mt={2} container columns={6} spacing={2} sx={{ display: isCruisePostPurchase ? 'none' : 'flex' }}>
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                {...register('price')}
                helperText={errors.price?.message}
                error={!!errors.price}
                disabled
                label="Sell price"
                fullWidth
                data-testid="sell-price"
              />
            </Grid>
            <Grid size={{ xs: 3, md: 1 }}>
              <Controller
                name="price_currency"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl error={fieldState.invalid} fullWidth>
                    <Autocomplete
                      {...field}
                      options={customerCurrencies}
                      autoHighlight
                      disableClearable
                      renderInput={(params) => <TextField {...params} label="Customer Currency" />}
                      onChange={(_, data) => {
                        handlePriceCurrencyChange(field.name, data);
                      }}
                      data-testid="customer-currency"
                    />
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid container direction="row" alignItems="center" size={{ xs: 3, md: 1 }}>
              <HtmlTooltip
                title={
                  <Stack direction="column" gap={1}>
                    <Typography variant="subtitle2">Currency conversion</Typography>
                    <Typography variant="body2">
                      {isBusinessTraveller
                        ? `= (Vendor Price + Margin) * (${priceCurrencyCode} / ${costPriceCurrencyCode})`
                        : `= (Vendor Price + Margin + Service Fee) * (${priceCurrencyCode} / ${costPriceCurrencyCode})`}
                    </Typography>
                    <Typography variant="body2">
                      {isBusinessTraveller
                        ? `= (${costPrice} + ${margin}) * (${customerExchangeRate} / ${vendorExchangeRate})`
                        : `= (${costPrice} + ${margin} + ${leServiceFee}) * (${customerExchangeRate} / ${vendorExchangeRate})`}
                    </Typography>
                    <Typography variant="body2">= {calculateCustomerPriceTotalAndCurrencyConvert()}</Typography>
                    {isBusinessTraveller && (
                      <Typography variant="body2" color="text.secondary">
                        Service fee is charged separately as a line item.
                      </Typography>
                    )}
                  </Stack>
                }
                placement="right"
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </HtmlTooltip>
            </Grid>
            {expectedCustomerCurrency && priceCurrencyCode !== expectedCustomerCurrency && (
              <Grid size={6}>
                <Alert severity="warning" data-testid="currency-mismatch-warning">
                  Customer is based in {user.country_code} — expected currency is {expectedCustomerCurrency}, but{' '}
                  {priceCurrencyCode} is selected. Please confirm this is intentional.
                </Alert>
              </Grid>
            )}
          </Grid>
          {!isCruisePostPurchase && isBusinessTraveller && (
            <CustomOfferBusinessTravellerServiceFeeSection
              userId={user.id_member}
              control={control}
              setValue={setValue}
              watch={watch}
              isSubmitting={isSubmitting}
              disabled={lockVendorPriceFieldsForPaidOrder}
            />
          )}
          {!isCruisePostPurchase && (
            <CustomOfferGstWidget
              control={control}
              errors={errors}
              hasOrders={hasOrders}
              isSubmitting={isSubmitting}
              watch={watch}
              australiaGstHelperText={
                isBusinessTraveller && itemType === 'hotel'
                  ? 'Price includes GST if Hotel is located in Australia'
                  : undefined
              }
            />
          )}
          {isCruisePostPurchase && (
            <CruisePostPurchaseSummary
              atiTicketingDisabled={isSubmitting}
              control={control}
              errors={errors}
              isCruiseRefare={isCruiseRefare}
              pricingDisabled={isSubmitting || lockVendorPriceFieldsForPaidOrder}
              register={register}
            />
          )}
          <CustomerPaymentsSection
            control={control}
            register={register}
            errors={errors}
            setValue={setValue}
            paymentFlowV2Errors={paymentFlowV2Errors}
            hasOrders={hasOrders}
            isSubmitting={isSubmitting}
            isCustomOfferPackage={isCustomOfferPackage}
            isBusinessTraveller={isBusinessTraveller}
            isEditMode={isEditMode}
            user={user}
            offer={offer}
            price={price}
            customerDepositAmount={customerDepositAmount}
            paymentDate={paymentDate}
            sectionState={customerPaymentSectionState}
            forceFullPayment={isCruisePostPurchase}
          />
        </>
      )}
      {isCustomOfferPackage && (
        <>
          <Box mt={2}>
            <Typography variant="h6">Customer Offer Price - {priceCurrencyCode}</Typography>
          </Box>
          <Grid mt={2} container columns={6} spacing={2}>
            <Grid size={{ xs: 3, md: 1 }}>
              <TextField
                {...register('payment_metadata.final_amount_customer_display')}
                helperText={errors.payment_metadata?.final_amount_customer_display?.message}
                error={!!errors.payment_metadata?.final_amount_customer_display}
                disabled={isSubmitting}
                label="Final amount to display"
                type="number"
                inputProps={{ step: 0.01 }}
                fullWidth
                required
                data-testid="final-amount-customer-display"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 1 }}>
              <Controller
                name="payment_metadata.block_customer_payment_notifications"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox {...field} checked={field.value} data-testid="dont-notify-customer-on-payment" />
                    }
                    label="Don't notify customer on payment"
                  />
                )}
              />
            </Grid>
          </Grid>
        </>
      )}
      {isEditMode && (
        <Grid mt={2} container columns={6} spacing={2}>
          <CustomOfferInternalDiscountField control={control} errors={errors} isSubmitting={isSubmitting} isReadOnly />
        </Grid>
      )}
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={6}>
          <TextField
            {...register('notes')}
            helperText={errors.notes?.message}
            error={!!errors.notes}
            disabled={isSubmitting}
            label="Internal notes"
            minRows={2}
            fullWidth
            multiline
            data-testid="internal-notes"
          />
        </Grid>
      </Grid>
      <ImagesForm
        images={images}
        hasHiddenToggle={false}
        showSaveButton={false}
        onAddImage={onAddImage}
        onUpdateImages={onUpdateImages}
        onDeleteImage={onDeleteImage}
      />
      <PropertyImagePrefillDialog
        isOpen={imageDialogOpen}
        handleClose={closeImageDialog}
        handleReplace={() => handleImageDialogAction('replace')}
        handleAppend={() => handleImageDialogAction('append')}
      />
      {order?.id_orders && (
        <OrderDetailNotes
          orderId={order?.id_orders}
          customer={user}
          refreshData={() => {
            setTimeout(() => window.location.reload(), 200);
          }}
          offerId={offer?.id}
        />
      )}
      {!isCustomOfferPackage && (
        <AccountsPayable
          control={control}
          isCruisePostPurchase={isCruisePostPurchase}
          isSubmitting={isSubmitting}
          setValue={setValue}
        />
      )}
      {!isCustomOfferPackage && (
        <Stack direction="row" spacing={2} my={2}>
          <FormGroup row>
            <Controller
              name="hiddenFromCustomer"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={isSubmitting}
                      data-testid="hidden-from-customer"
                    />
                  }
                  label="Hide from customer portal"
                />
              )}
            />
          </FormGroup>
          <CustomOfferLinkedPackageOfferField
            control={control}
            currentOffer={offer}
            userId={user.id_member}
            isSubmitting={isSubmitting}
          />
        </Stack>
      )}
      {isCustomOfferPackage && <CustomOfferPackageComponentsSection currentOffer={offer} userId={user.id_member} />}

      {isEditMode && (
        <Box my={2}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inclusionsDiscussed}
                  onChange={(e) => setInclusionsDiscussed(e.target.checked)}
                  disabled={hasOrders || isSubmitting}
                  data-testid="inclusions-discussed"
                />
              }
              label="Inclusions discussed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsExplained}
                  onChange={(e) => setTermsExplained(e.target.checked)}
                  disabled={hasOrders || isSubmitting}
                  data-testid="terms-explained"
                />
              }
              label="Terms and Conditions explained"
            />
          </FormGroup>
        </Box>
      )}

      <Stack mt={2} direction="row" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={2}>
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={isActionsDisabled}
            data-testid="save-button"
          >
            Save
          </Button>

          {showSendButton && (
            <Button
              type="button"
              startIcon={<CheckIcon />}
              variant="contained"
              color="secondary"
              disabled={isSendDisabled}
              onClick={onSendPayment}
              data-testid="confirm-button"
            >
              Confirm and show in my escapes
            </Button>
          )}

          {hasOrders && order?.id_orders && (
            <>
              <Button
                onClick={handleSendPaymentLink}
                disabled={paymentLinkSent || sendingPaymentLink}
                variant="text"
                data-testid="email-payment-link-button"
              >
                {sendingPaymentLink
                  ? 'Sending Email...'
                  : paymentLinkSent
                    ? 'Email payment link sent'
                    : 'Email payment link'}
              </Button>
              <Button
                onClick={handleSendPaymentLinkSms}
                disabled={paymentLinkSmsSent || sendingPaymentLinkSms}
                variant="text"
                data-testid="sms-payment-link-button"
              >
                {sendingPaymentLinkSms
                  ? 'Sending SMS...'
                  : paymentLinkSmsSent
                    ? 'SMS payment link sent'
                    : 'SMS payment link'}
              </Button>
            </>
          )}
        </Stack>

        {showSendButton && (
          <Typography color="error" sx={{ mt: 1 }}>
            Reminder: Send customer a payment link once Custom Offer is confirmed
          </Typography>
        )}
        {offer?.status === 'draft' && (
          <Stack direction="row" spacing={2}>
            <Button
              type="button"
              variant="contained"
              color="error"
              disabled={isActionsDisabled}
              onClick={openDeleteDialog}
              data-testid="delete-button"
            >
              Delete
            </Button>
          </Stack>
        )}
        <ConfirmDeleteDialog
          isOpen={deleteDialogOpen}
          description={offer?.name}
          handleClose={closeDeleteDialog}
          handleDelete={deleteOffer}
        />
      </Stack>
    </form>
  );
}
